/**
 * Equipment types — the kinds of machine a company owns.
 *
 * Aaron: "Same for equipment — but icons should be selected on categories of
 * equipment not each piece." A company owns one kind of 72-inch Z-turn and
 * forty of the machines, so the mark belongs to the kind; this is the screen
 * that picks it, and the Equipment list is where forty rows then read as
 * mowers at a glance.
 *
 * Types existed before this page but had no editor — they came back from
 * `GET /equipment/lookups` and could only be created by a seed. So this is
 * also the first place a company can name one.
 *
 * **Three fields board 4 asks for are not here**, and deliberately: "Service
 * every", "Quick check" and "Needs a daily quick check". None of them is a
 * column on `equipment_types` — a service interval is a
 * `service_schedule_templates` row with its own lines, and a quick check is a
 * list of `quick_check_items` — so each is a sub-record list rather than an
 * input in a flyout, and putting a single number in front of somebody would
 * promise one interval where the schema holds many. They belong on an
 * equipment-type record page, which does not exist yet.
 */
import { useState } from 'react'
import type { MeResponse } from '@rowted/shared'
import {
  EQUIPMENT_FAMILY_COLOURS, EQUIPMENT_FAMILY_DEFAULTS, FALLBACK_COLOUR, FALLBACK_GLYPH,
  isEquipmentGlyph, isPaletteColour,
} from '@rowted/shared'
import { CatalogList } from '../components/catalog-list.js'
import { MarkTile } from '../components/landing-marks.js'
import type { ListRow } from '../lib/use-list.js'
import { useSettingsBar } from '../settings/settings-bar.js'

const FAMILIES = [
  { value: 'truck', label: 'Trucks' },
  { value: 'trailer', label: 'Trailers' },
  { value: 'mower', label: 'Mowers' },
  { value: 'handheld', label: 'Handhelds' },
  { value: 'loader', label: 'Loaders' },
  { value: 'attachment', label: 'Attachments' },
  { value: 'other', label: 'Other' },
]

const METERS = [
  { value: 'hours', label: 'Hours' },
  { value: 'miles', label: 'Miles' },
  { value: 'none', label: 'No meter' },
]

/** The tile every machine of this kind wears. */
const typeMark = (row: ListRow) => (
  <MarkTile
    set="equipment"
    slug={typeof row.icon === 'string' ? row.icon : null}
    colour={typeof row.colour === 'string' ? row.colour : null}
    size={28}
    title={String(row.name ?? 'Equipment type')}
  />
)

const nameOnly = (row: ListRow) => ({ primary: String(row.name ?? '') })

export function EquipmentTypes({ me }: { me: MeResponse }) {
  const [reloadKey, setReloadKey] = useState(0)
  const canEdit = me.permissions.company_settings === 'edit'
    || me.permissions.company_settings === 'full'

  useSettingsBar({
    title: 'Equipment types',
    sub: 'the kinds of machine, and the icon every one of them wears',
  })

  return (
    <div className="p-4">
      <CatalogList
        area="catalog-admin/equipment-types"
        title="Equipment types"
        singular="equipment type"
        canEdit={canEdit}
        importOrgUnitId={me.scope.selected[0]}
        reloadKey={reloadKey}
        onChanged={() => setReloadKey((key) => key + 1)}
        primary={nameOnly}
        mark={{ render: typeMark }}
        alsoFetch={['icon', 'colour']}
        blank={{
          name: '', family: 'other', meterKind: 'hours', description: '',
          sortOrder: 0, active: true,
          /*
           * The family's own suggestion, because `family` starts at a value
           * and a blank icon beside a chosen family would be a question the
           * form already knows the answer to. Changing the family re-suggests
           * both until somebody picks by hand (`suggestOn` below).
           */
          icon: EQUIPMENT_FAMILY_DEFAULTS['other']!,
          colour: EQUIPMENT_FAMILY_COLOURS['other']!,
        }}
        fields={[
          { name: 'name', label: 'Name', required: true, placeholder: '72-inch Z-turn' },
          {
            name: 'family', label: 'Family', kind: 'select', required: true,
            options: FAMILIES,
            hint: 'what kind of machine it is · decides the icon and colour we suggest',
            /*
             * Board 4: changing the family re-suggests the mark **unless
             * somebody has already picked one by hand**. Without that rule
             * either the suggestion is useless (it never moves) or the choice
             * is (it gets overwritten).
             */
            suggests: {
              /* The shared fallbacks, never a hex typed here — the design-token
                 guard is right to refuse one, and a second copy of slate would
                 be a colour that could drift from the palette. */
              icon: (value: string) => EQUIPMENT_FAMILY_DEFAULTS[value] ?? FALLBACK_GLYPH,
              colour: (value: string) => EQUIPMENT_FAMILY_COLOURS[value] ?? FALLBACK_COLOUR,
            },
          },
          {
            name: 'meterKind', label: 'Meter', kind: 'select', required: true,
            options: METERS,
            hint: 'what its meter counts · a trailer has none, a truck counts miles',
          },
          {
            name: 'icon', label: 'Icon', kind: 'icon', set: 'equipment', required: true,
            hint: 'one of the equipment set · the glyph on every machine of this kind',
          },
          {
            name: 'colour', label: 'Colour', kind: 'mark-colour', required: true,
            hint: 'the tint behind the icon',
          },
          { name: 'preview', label: '', kind: 'preview', set: 'equipment' },
          { name: 'description', label: 'Description', kind: 'textarea' },
          { name: 'sortOrder', label: 'Sort order', kind: 'number' },
          { name: 'active', label: 'Active', kind: 'switch' },
        ]}
        emptyTitle="No equipment types yet"
        emptyBody="A type is a kind of machine — one 72-inch Z-turn, forty of the machines. It carries the icon every one of them wears."
      />
    </div>
  )
}

/* Kept beside the fields so the two refinements the server applies are visible
   from the screen that has to satisfy them. */
export const EQUIPMENT_TYPE_GUARDS = { isEquipmentGlyph, isPaletteColour }
